import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import "./ManageEmployees.css";
import CustomAlert from "../../custom_alert/CustomAlert";
import TransLoader from "../../loader/TransLoader";

export default function ManageEmployees() {
  const [config, setConfig] = useState(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [inactiveEmployees, setInactiveEmployees] = useState([]);
  const [configData, setConfigData] = useState({ roles: [], departments: [] });
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateData, setUpdateData] = useState({
    empCode: "",
    name: "",
    role: "",
    department: "",
    joining_date: "",
    nda_signed: 0,
    nda_signed_date: "",
  });
  const [formData, setFormData] = useState({
    empCode: "",
    name: "",
    role: "",
    department: "",
    joining_date: "",

  });

  // ------------------------------
  // Fetch Employees
  // ------------------------------
  const fetchEmployees = async () => {
    if (!config) return;
    try {
      setLoading(true);
      const activeRes = await fetch(`${config.BASE_URL}${config.GET_EMPLOYEES}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "RC_EMP_NEW" },
        body: JSON.stringify({ active: 1 }),
      });
      const inactiveRes = await fetch(`${config.BASE_URL}${config.GET_EMPLOYEES}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "RC_EMP_NEW" },
        body: JSON.stringify({ active: 0 }),
      });

      const activeData = await activeRes.json();
      const inactiveData = await inactiveRes.json();
      setActiveEmployees(activeData.data || []);
      setInactiveEmployees(inactiveData.data || []);
    } catch (err) {
      console.error(err);
      setAlertMessage("Failed to fetch employee data from server.");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------
  // Update Employee Handlers
  // ------------------------------
  const handleUpdate = (employee) => {
    setUpdateData({
      empCode: employee.emp_code,
      name: employee.name,
      role: employee.role,
      department: employee.department,
      nda_signed: employee.nda_signed || 0,
      nda_signed_date: employee.nda_signed_date || "",
      joining_date: employee.joining_date || "",

    });
    setShowUpdateModal(true);
    console.log(employee.joining_date);

  };

  const handleUpdateChange = (e) => {
    const { name, value } = e.target;
    setUpdateData((prev) => ({ ...prev, [name]: value }));
  };

  const saveUpdate = async () => {
    if (!config) return;
    if (updateData.nda_signed === 1 && !updateData.nda_signed_date) {
      setAlertMessage("Please provide NDA signed date.");
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${config.BASE_URL}${config.UPDATE_EMPLOYEE}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "RC_EMP_NEW" },
        body: JSON.stringify({
          emp_code: updateData.empCode,
          role: updateData.role,
          department: updateData.department,
          nda_signed: updateData.nda_signed,
          nda_signed_date: updateData.nda_signed_date,
          joining_date: updateData.joining_date, 
        }),
      });
      const data = await res.json();
      setAlertMessage(data.message || "Employee updated successfully.");
      setShowUpdateModal(false);
      fetchEmployees();
    } catch (err) {
      console.error(err);
      setAlertMessage("Server error occurred. Contact admin.");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------
  // Deactivate Employee
  // ------------------------------
  const handleDeactivate = async (empCode) => {
    setLoading(true);
    try {
      const res = await fetch(`${config.BASE_URL}${config.DEACTIVE_EMPLOYEE}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "RC_EMP_NEW" },
        body: JSON.stringify({ emp_code: empCode }),
      });
      const data = await res.json();
      if (data.status === "success") {
        const emp = activeEmployees.find((e) => e.emp_code === empCode);
        if (emp) {
          setActiveEmployees((prev) => prev.filter((e) => e.emp_code !== empCode));
          setInactiveEmployees((prev) => [...prev, { ...emp, deactivated_at: new Date().toISOString().split("T")[0] }]);
        }
        setAlertMessage(`Employee ${empCode} deactivated successfully.`);
      } else {
        setAlertMessage(data.message || "Failed to deactivate employee.");
      }
    } catch (err) {
      console.error(err);
      setAlertMessage("Server error occurred. Contact admin.");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------
  // Config + Roles/Departments
  // ------------------------------
  useEffect(() => {
    fetch("/config.json").then((res) => res.json()).then(setConfig).catch(console.error);
    fetch("/roles_depts.json").then((res) => res.json()).then((data) => setConfigData({ roles: data.roles || [], departments: data.departments || [] })).catch(console.error);
  }, []);

  useEffect(() => { fetchEmployees(); }, [config]);

  // ------------------------------
  // Form input handlers
  // ------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenAdd = async () => {
    if (!config) return;
    setLoading(true);
    try {
      const response = await fetch(`${config.BASE_URL}${config.GEN_NEW_EMP_ID}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "RC_EMP_NEW" },
      });
      const data = await response.json();
      if (data.status === "success") {
        setFormData({ empCode: data.employee_id, name: "", role: "", department: "" , joining_date: ""});
        setShowAddModal(true);
      } else {
        setAlertMessage(data.message || "Failed to generate Employee ID.");
      }
    } catch (err) {
      console.error(err);
      setAlertMessage("Failed to generate Employee ID.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.empCode || !formData.name || !formData.role || !formData.department) {
      setAlertMessage("Please fill all fields.");
      return;
    }
    if (!config) {
      setAlertMessage("Configuration not loaded. Try again.");
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${config.BASE_URL}${config.ADD_EMPLOYEE}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "RC_EMP_NEW" },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.status === "success") {
        setAlertMessage(`Employee ${data.emp_code} added successfully! Default password: "user@123"`);
        setShowAddModal(false);
        setFormData({ empCode: "", name: "", role: "", department: "" ,joining_date: ""});
        setActiveEmployees((prev) => [...prev, data]);
      } else {
        setAlertMessage(data.message || "Failed to add employee.");
      }
    } catch (err) {
      console.error(err);
      setAlertMessage("Server error occurred. Contact admin.");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------
  // Table Columns
  // ------------------------------
  const activeColumns = [
    { name: "Emp Code", selector: (row) => row.emp_code, sortable: true },
    { name: "Name", selector: (row) => row.name, sortable: true },
    { name: "Role", selector: (row) => row.role, sortable: true },
    { name: "Department", selector: (row) => row.department, sortable: true },
    { name: "Joining Date", selector: (row) => row.joining_date, sortable: true },
    {
      name: "NDA",
      selector: row => row.nda_signed === 1 ? row.nda_signed_date : "NDA not signed",
      cell: row => row.nda_signed === 1 ? <span>{row.nda_signed_date}</span> : <span style={{ color: "red" }}>NDA not signed</span>,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <button className="update-btn" onClick={() => handleUpdate(row)}>Update</button>
          <button className="deactivate-btn" onClick={() => handleDeactivate(row.emp_code)}>Deactivate</button>
        </>
      ),
    },
  ];

  const inactiveColumns = [
    { name: "Emp Code", selector: row => row.emp_code, sortable: true },
    { name: "Name", selector: row => row.name, sortable: true },
    { name: "Role", selector: row => row.role, sortable: true },
    { name: "Department", selector: row => row.department, sortable: true },
    { name: "Deactivated At", selector: row => row.deactivated_at, sortable: true },
    {
      name: "NDA",
      selector: row => row.nda_signed === 1 ? row.nda_signed_date : "NDA not signed",
      cell: row => row.nda_signed === 1 ? <span>{row.nda_signed_date}</span> : <span style={{ color: "red" }}>NDA not signed</span>,
    },
  ];

  // ------------------------------
  // Search filters
  // ------------------------------
  const [activeSearch, setActiveSearch] = useState("");
  const [inactiveSearch, setInactiveSearch] = useState("");

  const filteredActive = activeEmployees.filter((emp) =>
    Object.values(emp).some((val) => val.toString().toLowerCase().includes(activeSearch.toLowerCase()))
  );

  const filteredInactive = inactiveEmployees.filter((emp) =>
    Object.values(emp).some((val) => val.toString().toLowerCase().includes(inactiveSearch.toLowerCase()))
  );

  return (
    <>
      <div className="manage-employees-container">
        <button className="add-btn" onClick={handleOpenAdd}>Add Employee</button>

        {/* Add Employee Modal */}
        {showAddModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              {loading ? <TransLoader /> : (
                <>
                  <h2>Add Employee</h2>
                  <form onSubmit={handleSave}>
                    <label>Employee Code</label>
                    <input type="text" value={formData.empCode} readOnly />

                    <label>Name</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} />

                    <label>Role</label>
                    <select name="role" value={formData.role} onChange={handleChange}>
                      <option value="">Select Role</option>
                      {configData.roles.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>

                    <label>Department</label>
                    <select name="department" value={formData.department} onChange={handleChange}>
                      <option value="">Select Department</option>
                      {configData.departments.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>

                    <label>Joining Date</label>
                    <input
                      type="date"
                      name="joining_date"
                      value={formData.joining_date}
                      onChange={handleChange}
                    />


                    <div className="modal-buttons">
                      <button type="submit">Save</button>
                      <button type="button" onClick={() => setShowAddModal(false)}>Cancel</button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        <CustomAlert message={alertMessage} onClose={() => setAlertMessage("")} />
      </div>

      {loading ? (
        <TransLoader />
      ) : (
        <>
          <h3>Active Employees (Total: {filteredActive.length})</h3>
          <input
            type="text"
            placeholder="Search active employees..."
            value={activeSearch}
            onChange={(e) => setActiveSearch(e.target.value)}
            className="table-search"
          />
          <DataTable
            columns={activeColumns}
            data={filteredActive}
            pagination
            highlightOnHover
            selectableRows
            responsive
            defaultSortField="emp_code"
          />

          <h3>Inactive Employees (Total: {filteredInactive.length})</h3>
          <input
            type="text"
            placeholder="Search inactive employees..."
            value={inactiveSearch}
            onChange={(e) => setInactiveSearch(e.target.value)}
            className="table-search"
          />
          <DataTable
            columns={inactiveColumns}
            data={filteredInactive}
            pagination
            highlightOnHover
            selectableRows
            responsive
            defaultSortField="emp_code"
          />

          {/* Update Modal */}
          {showUpdateModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                {loading ? <TransLoader /> : (
                  <>
                    <h2>Update Employee</h2>
                    <form
                      onSubmit={(e) => { e.preventDefault(); saveUpdate(); }}
                    >
                      <label>Employee Code</label>
                      <input type="text" value={updateData.empCode} readOnly />

                      <label>Name</label>
                      <input type="text" value={updateData.name} readOnly />

                      <label>Role</label>
                      <select name="role" value={updateData.role} onChange={handleUpdateChange}>
                        <option value="">Select Role</option>
                        {configData.roles.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>

                      <label>Department</label>
                      <select name="department" value={updateData.department} onChange={handleUpdateChange}>
                        <option value="">Select Department</option>
                        {configData.departments.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>

                      <label>Joining Date</label>
                      <input
                        type="date"
                        name="joining_date"
                        value={updateData.joining_date}
                        onChange={handleUpdateChange}
                      />

                      <label>
                        NDA Signed:
                        <input
                          type="checkbox"
                          checked={updateData.nda_signed === 1}
                          onChange={(e) =>
                            setUpdateData(prev => ({
                              ...prev,
                              nda_signed: e.target.checked ? 1 : 0,
                              nda_signed_date: e.target.checked ? prev.nda_signed_date : ""
                            }))
                          }
                        />
                      </label>

                      <label>
                        NDA Signed Date:
                        <input
                          type="date"
                          value={updateData.nda_signed_date}
                          onChange={(e) =>
                            setUpdateData(prev => ({ ...prev, nda_signed_date: e.target.value }))
                          }
                          disabled={updateData.nda_signed === 0}
                        />
                      </label>

                      <div className="modal-buttons">
                        <button type="submit">Save</button>
                        <button type="button" onClick={() => setShowUpdateModal(false)}>Cancel</button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
